/**
 * Signup intent capture — fire-and-forget client helper.
 *
 * Called twice from the Signup page:
 *   - Step 1 continue: captures the email as a "pending lead" so the
 *     `send-signup-reminder` cron can follow up if the user drops before
 *     step 2.
 *   - Post-submit: marks `completed_at` so the cron stops targeting them.
 *
 * NEVER throws. NEVER blocks. Failures are logged but silently swallowed —
 * the user-facing auth flow must not regress because of a lead-capture
 * hiccup.
 */
import { supabase } from './supabase'

type IntentPayload = {
  email: string
  completed?: boolean
  source?: 'signup' | 'login'
}

function readUtm(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
  if (typeof window === 'undefined') return {}
  try {
    const params = new URLSearchParams(window.location.search)
    const utm: Record<string, string> = {}
    const src = params.get('utm_source')
    const med = params.get('utm_medium')
    const camp = params.get('utm_campaign')
    if (src) utm.utm_source = src.slice(0, 120)
    if (med) utm.utm_medium = med.slice(0, 120)
    if (camp) utm.utm_campaign = camp.slice(0, 120)
    return utm
  } catch {
    return {}
  }
}

function detectLocale(): 'fr' | 'en' {
  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement.lang?.toLowerCase() ?? ''
    if (htmlLang.startsWith('en')) return 'en'
    if (htmlLang.startsWith('fr')) return 'fr'
  }
  if (typeof navigator !== 'undefined') {
    const nav = navigator.language?.toLowerCase() ?? ''
    if (nav.startsWith('en')) return 'en'
  }
  return 'fr'
}

export async function captureSignupIntent(payload: IntentPayload): Promise<void> {
  const email = payload.email?.trim().toLowerCase()
  if (!email) return

  const base = import.meta.env.VITE_SUPABASE_URL as string | undefined
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined
  if (!base || !anonKey) return

  try {
    // Best-effort attach session token if the user happens to be authenticated
    // (post-submit call may run just after signUp creates a session). Not
    // required for the endpoint — it only needs the anon key — but keeps
    // the request symmetrical with the rest of the app.
    const { data: sessionData } = await supabase.auth.getSession().catch(() => ({ data: { session: null } }))
    const token = sessionData?.session?.access_token ?? anonKey

    await fetch(`${base}/functions/v1/capture-signup-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({
        email,
        locale: detectLocale(),
        source: payload.source ?? 'signup',
        completed: payload.completed === true,
        ...readUtm(),
      }),
      // Keep it strictly non-blocking — browsers GC in-flight fetches if the
      // user navigates away, which is exactly what we want for step 1.
      keepalive: true,
    })
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[signupIntent] capture failed (non-blocking):', err)
    }
  }
}
